import { dayjs } from '@karve.it/core';

import { BaseFieldFragment, Job_CalendarEventFragment } from '../../../generated/graphql.generated';
import { getFieldValue, splitName } from '../../fields/fields.utils';
import { JOB_ROLE_MAP } from '../../global.constants';
import { formatLocationToDisplay, getAccessInformation, getBuildingDetails } from '../../jobs/jobs.util';

import { formatPhoneNumber } from '../../shared/phone.pipe';
import { convertSecondsToTimeUnit } from '../../shared/seconds-to-time-unit.pipe';

import { formatEventDetails } from './job-event-details.util';

export interface DetailedLocation {
    id: string;
    locationType: string;
    addressString: string;
    details: string;
    parkingInformation: string;
    accessInformation: string;
    // ... other location props
}

export interface JobLocations {
    start: DetailedLocation;
    end: DetailedLocation;
    dock: DetailedLocation;
    otherStops: DetailedLocation[];
}

export const mapJobToTemplateVariables = (
    job: any, // Using any to support both FullJobFragmentWithFields and JobDetailsV2Fragment
    eventOverride?: Job_CalendarEventFragment
) => {
    // 1. Select Customer
    const jobCustomer = job?.users?.find((u) => u.role === JOB_ROLE_MAP.customerRole);
    const customer = jobCustomer?.user;
    
    let customerName = 'No Customer';
    if (customer) {
        customerName = [
            customer.givenName,
            customer.familyName,
        ]
        .map((s) => s?.trim())
        .filter(Boolean)
        .join(' ');
    }

    // 2. Select Sales Agent
    const salesAgentUser = job?.users?.find((user) => user.role === JOB_ROLE_MAP.salesAgentRole)?.user;
    let salesAgent;
    if (salesAgentUser) {
        salesAgent = {
            ...salesAgentUser,
            name: [salesAgentUser.givenName, salesAgentUser.familyName].filter(Boolean).join(' ')
        };
    }

    // 3. Select Estimator
    const estimatorUser = job?.users?.find((user) => user.role === JOB_ROLE_MAP.estimatorRole)?.user;
    let estimator;
    if (estimatorUser) {
        estimator = {
            ...estimatorUser,
            name: [estimatorUser.givenName, estimatorUser.familyName].filter(Boolean).join(' ')
        };
    }

    // 4. Job Source
    const sourceField = job?.fields?.find((field) => field.name === 'customer.howDidTheyHearAboutUs');
    const jobSource = sourceField?.values?.[0]?.value || 'Not set';

    // 5. Locations
    const jobLocations = {
        otherStops: [],
    } as JobLocations;

    if (job) {
        job?.locations?.forEach((locationObject) => {
            const { locationType, location } = locationObject;
            if (['start', 'end', 'dock'].includes(locationType)) {
                const addressString = formatLocationToDisplay(locationObject.location);
                jobLocations[locationType] = {
                    ...location,
                    locationType,
                    addressString,
                };
            } else {
                jobLocations.otherStops.push({
                    ...location,
                    locationType,
                    addressString: formatLocationToDisplay(locationObject.location),
                } as DetailedLocation);
            }
        });

        const locationFields = {
            startLocation: {
                parkingInformation: '',
            },
            endLocation: {
                parkingInformation: '',
            },
        };

        // Process each field and assign to respective location object
        job?.fields?.forEach((field) => {
            if (field.name.startsWith('startLocation.') || field.name.startsWith('endLocation.')) {
                const { namespace, key } = splitName(field as BaseFieldFragment);
                if (locationFields[namespace]) {
                    locationFields[namespace][key] = getFieldValue(field);
                }
            }
        });

        if (jobLocations.start) {
            jobLocations.start.details = getBuildingDetails(locationFields.startLocation);
            jobLocations.start.parkingInformation = locationFields.startLocation?.parkingInformation;
            jobLocations.start.accessInformation = getAccessInformation(locationFields.startLocation);
        }

        if (jobLocations.end) {
            jobLocations.end.details = getBuildingDetails(locationFields.endLocation);
            jobLocations.end.parkingInformation = locationFields.endLocation?.parkingInformation;
            jobLocations.end.accessInformation = getAccessInformation(locationFields.endLocation);
        }
    }

    // 6. Events & Move Date
    const timezone = job?.zone?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
    const events = (job?.events || []).map(event => formatEventDetails(event, timezone));
    
    // Find move date logic
    // If eventOverride is provided, use it.
    // Otherwise use timeline or first revenue generating event.
    let moveDate;
    let firstLocation;

    if (eventOverride) {
        moveDate = eventOverride.start;
    }

    const firstRevenueGeneratingEvent = events?.find((e) => e.event.total);
    
    if (!moveDate) {
         moveDate = job?.timeline ? dayjs(job.timeline).unix() : firstRevenueGeneratingEvent?.event?.start;
    }

    // Travel time logic (from reducer)
    // Note: This relies on 'locations' array structure in the event.
    // formatEventDetails returns locations sorted by order.
    const relevantEvent = eventOverride 
        ? events.find(e => e.event.id === eventOverride.id) 
        : firstRevenueGeneratingEvent;

    if (relevantEvent) {
         firstLocation = relevantEvent.locations?.[0]?.type === 'basestart'
            ? relevantEvent.locations?.[1]
            : relevantEvent.locations?.[0];
    }

    return {
        job,
        misc: {
            salesAgent,
            estimator,
            moveDate,
            travelTimeToFirstLocation: firstLocation?.totalTravelTimeToNextLocation != null
                ? convertSecondsToTimeUnit(firstLocation.totalTravelTimeToNextLocation).trim()
                : null,
            customer: {
                ...customer,
                name: customerName,
                phone: formatPhoneNumber(customer?.phone, true),
            },
            locations: jobLocations,
            howDidTheyHearAboutUs: jobSource,
        }
    };
};
